import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import isEqual from 'lodash.isequal';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import debug from 'debug';

// Logger
const logForm = debug('rois:newcommunication:form');

// Utils
import { useCacheFormInSessionStorage } from '../../../hooks/useCacheForm';
import { createCommunicationSchema } from './schema';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { client } from '../../../gql/client';
import { GET_ORDER_BY_ID } from '../../../gql/queries/orders/orders';
import { GET_COMMUNICATION_BY_ID } from '../../../gql/queries/communication';
import { GET_GENERIC_ORDER_BY_ID } from '../../../gql/queries/genericOrders';
import { GET_CAMPAIGN_BY_ID } from '../../../gql/queries/campaign';

// Types
import { NewCommunication, CommunicationSendStatus } from '../../../types/communication';
import { Props, AutoFillData } from './types';
import { ImperativeRefSearchableDropdown } from '../../inputs/SearchableDropdown/types';
import { ImperativeRefMultiSelect } from '../../inputs/DropDown/MultiSelectDropdown/types';

// Components
import BasicInfo from './BasicInfo';
import { Description } from './Description';
import { CreateOT } from './CreateOT';

// Styles
import { DoubleColumnContainer, ContainerWithButton } from './styles';

// Redux
import { getCurrentIndexTabFormByTab } from '../../../state/selectors/ui/tabs';
import { getCurrentRowDataModal } from '../../../state/selectors/ui/modal';
import { selectedRowModalTable } from '../../../state/actions/ui/selectRowTableModal';
import {
  ButtonsContainer,
  ContainerForm,
  FieldsContainer,
} from '../components/MainStyled';
import { MainButton } from '../../inputs/buttons/MainButton';
import { formNaming } from './constants';

const NewCommunicationForm = ({
  onFormSubmit,
  initialData,
  namingPersistForm,
  namingTabsType,
  isNecessaryTagSpecial = false,
}: Props) => {
  const dispatch = useDispatch();

  // Habilitar logs en desarrollo
  if (import.meta.env.DEV) {
    debug.enable('rois:newcommunication:*');
  }

  const { t } = useTranslation('forms');
  const selectorIndexTab = useSelector(
    getCurrentIndexTabFormByTab(namingTabsType),
    isEqual
  );

  // FIX BUG #35 (T60/T68): Override selector value when coming from OT/Campaign
  // Redux dispatch timing is unreliable (Redux Persist rehidrates AFTER dispatch),
  // so we use initialData.parentOrderId directly to determine the initial tab
  // Detect Campaign parent: parentOrderType (edit/duplicate) or parentOrderRoid prefix (new)
  const isCampaignParent =
    initialData.parentOrderType === 'campaign' ||
    (initialData as any)?.parentOrderRoid?.startsWith?.('Ca');

  // Once the user has clicked a tab manually, trust Redux (selectorIndexTab) — esto permite
  // cambiar a Tab 0 ("Datos de la O.T.") cuando se desliga la relación. Antes del primer
  // clic del usuario, forzamos el tab derivado de parentOrderId para que la preselección
  // sobreviva a Redux Persist + auto-dispatch de Tabs en mount.
  // El ref se actualiza vía `onUserClick` de Tabs (sólo se dispara en clic real, NO en mount
  // ni en sync por defaultIndex).
  const userClickedTab = useRef(false);
  const handleUserClickTab = useCallback(() => {
    userClickedTab.current = true;
  }, []);

  const effectiveIndexTab = userClickedTab.current
    ? selectorIndexTab
    : initialData.parentOrderId
      ? (isCampaignParent ? 2 : 1)
      : selectorIndexTab;

  // Log del formulario
  logForm('🟢 [NewCommunicationForm] RENDER');
  logForm('  - namingTabsType:', namingTabsType);
  logForm('  - selectorIndexTab (de Redux):', selectorIndexTab);
  logForm('  - userClickedTab.current:', userClickedTab.current);
  logForm('  - effectiveIndexTab:', effectiveIndexTab);
  logForm('  - initialData.parentOrderId:', initialData.parentOrderId);

  // Calculate correct modal keys for OT and Campaign based on form type
  const otModalKey =
    namingPersistForm === formNaming.NEW_COMMUNICATION_FORM
      ? formNaming.NEW_COMMUNICATION_FORM_OT
      : namingPersistForm === formNaming.EDIT_COMMUNICATION_FORM
        ? formNaming.EDIT_COMMUNICATION_FORM_OT
        : namingPersistForm === formNaming.NEW_DUPLICATE_COMMUNICATION_FORM
          ? formNaming.NEW_DUPLICATE_COMMUNICATION_FORM_OT
          : namingPersistForm;

  const campaignModalKey =
    namingPersistForm === formNaming.NEW_COMMUNICATION_FORM
      ? formNaming.NEW_COMMUNICATION_FORM_CAMPAIGN
      : namingPersistForm === formNaming.EDIT_COMMUNICATION_FORM
        ? formNaming.EDIT_COMMUNICATION_FORM_CAMPAIGN
        : namingPersistForm === formNaming.NEW_DUPLICATE_COMMUNICATION_FORM
          ? formNaming.NEW_DUPLICATE_COMMUNICATION_FORM_CAMPAIGN
          : namingPersistForm;

  const directionRequired = useFeatureFlag('directionRequired', true);

  // Get Redux data from the correct keys
  const selectorDataOT = useSelector(
    getCurrentRowDataModal(otModalKey),
    isEqual
  );

  const selectorDataCampaign = useSelector(
    getCurrentRowDataModal(campaignModalKey),
    isEqual
  );

  const {
    handleSubmit,
    register,
    control,
    setValue,
    formState: { errors },
  } = useForm<NewCommunication>({
    defaultValues: {
      ...initialData,
      intent: 'pass-data',
      // La fecha la decide el loader (`resolveFreshRegisterDate`), también al
      // duplicar. Antes el duplicado la forzaba aquí a «ahora» en CADA montaje,
      // y una hora cambiada a mano se perdía al volver con «Editar».
      registerInitDate: initialData.registerInitDate,
    },
    resolver: yupResolver(createCommunicationSchema(directionRequired)) as any,
    context: {
      index: effectiveIndexTab,
      dataOT:
        effectiveIndexTab === 1
          ? selectorDataOT?.data
          : effectiveIndexTab === 2
            ? selectorDataCampaign?.data
            : undefined,
      isCommunicationDuplicate: initialData.isCommunicationDuplicate ?? false,
    },
  });

  useCacheFormInSessionStorage<NewCommunication>(namingPersistForm, control);

  // BUG 24 FIX: Populate Redux modalTable when coming from ShowOT
  // so that SelectionOT visually displays the pre-selected OT/Campaign immediately.
  // Tarea #8 (2026-04-24): run-once guard — evita el ciclo papelera→repopula→papelera
  // cuando el usuario deselecciona explícitamente la OT/Campaña con el icono basura.
  const hasInitializedFromParent = useRef(false);
  useEffect(() => {
    if (hasInitializedFromParent.current) return;
    // Mark init as done at the start, regardless of whether we dispatch:
    // prevents papelera→repopula loop even if Redux was already populated on mount.
    hasInitializedFromParent.current = true;
    if (initialData.parentOrderId && (initialData as any).parentOrderRoid) {
      const isCampaign =
        initialData.parentOrderType === 'campaign' ||
        (initialData as any)?.parentOrderRoid?.startsWith?.('Ca');

      const targetModalKey = isCampaign ? campaignModalKey : otModalKey;

      const currentReduxData = isCampaign ? selectorDataCampaign : selectorDataOT;

      // Only dispatch if Redux is currently empty for this key, to prevent overriding manual selection
      if (!currentReduxData?.data || !currentReduxData?.isShowedData) {
        dispatch(
          selectedRowModalTable(targetModalKey, {
            isShowedData: true,
            data: {
              id: initialData.parentOrderId,
              roid: (initialData as any).parentOrderRoid,
            }
          })
        );
      }
    }
  }, [
    initialData,
    campaignModalKey,
    otModalKey,
    dispatch,
    selectorDataCampaign?.data,
    selectorDataCampaign?.isShowedData,
    selectorDataOT?.data,
    selectorDataOT?.isShowedData
  ]);

  // T39: Auto-fill direction from parent order when creating related communication
  useEffect(() => {
    if (selectorDataOT?.data?.direction && !initialData.direction) {
      setValue('direction', selectorDataOT.data.direction);
    }
  }, [selectorDataOT?.data?.direction, initialData.direction, setValue]);

  // Refs for BasicInfo SearchableDropdowns (needed for visual auto-fill)
  const groupInterRef = useRef<ImperativeRefSearchableDropdown>(null);
  const mediaRef = useRef<ImperativeRefSearchableDropdown>(null);
  // Ref del selector Equipo (tab 0, múltiple) para sincronizar la selección tras setValue
  const teamDropDownRef = useRef<ImperativeRefMultiSelect>(null);

  // Auto-fill state
  const [autoFillData, setAutoFillData] = useState<AutoFillData>({});

  // Show auto-fill button ONLY for NEW communications with parent order
  // (Not for duplicates or edits - following Actions/Accidents pattern)
  const showAutoFillButton =
    namingPersistForm === formNaming.NEW_COMMUNICATION_FORM &&
    !!initialData.parentOrderId;

  // Fetch parent order data and genericOrder/campaign for auto-fill
  const fetchDataToAutoFill = useCallback(async () => {
    if (!initialData.parentOrderId) return undefined;

    const orderResult = await client.query({
      query: GET_ORDER_BY_ID,
      variables: {
        id: initialData.parentOrderId,
      },
    });

    const order = orderResult.data.order;

    // If order has genericOrders, fetch the first one for auto-fill (like Action/Accident)
    if (order.genericOrdersIDs && order.genericOrdersIDs.length > 0) {
      const genericOrderResult = await client.query({
        query: GET_GENERIC_ORDER_BY_ID,
        variables: {
          id: order.genericOrdersIDs[0],
        },
      });

      // Also fetch first communication if exists (for BasicInfo fields)
      let communication;
      if (order.communicationIDs && order.communicationIDs.length > 0) {
        const communicationResult = await client.query({
          query: GET_COMMUNICATION_BY_ID,
          variables: {
            id: order.communicationIDs[0],
          },
        });
        communication = communicationResult.data.communication;
      }

      return {
        order,
        genericOrder: genericOrderResult.data.genericOrder,
        communication,
      };
    }

    // If order has campaigns, fetch the first one for auto-fill
    if (order.campaignIDs && order.campaignIDs.length > 0) {
      const campaignResult = await client.query({
        query: GET_CAMPAIGN_BY_ID,
        variables: {
          id: order.campaignIDs[0],
        },
      });

      // Also fetch first communication if exists (for BasicInfo fields)
      let communication;
      if (order.communicationIDs && order.communicationIDs.length > 0) {
        const communicationResult = await client.query({
          query: GET_COMMUNICATION_BY_ID,
          variables: {
            id: order.communicationIDs[0],
          },
        });
        communication = communicationResult.data.communication;
      }

      return {
        order,
        campaignData: campaignResult.data.campaign,
        communication,
      };
    }

    return { order };
  }, [initialData.parentOrderId]);

  // Handle auto-fill button click
  const autoFillFunction = useCallback(async () => {
    try {
      const result = await fetchDataToAutoFill();
      if (result) {
        // Actualizar autoFillData — CreateOT tiene un useEffect que reacciona a este
        // cambio y rellena predefine/classification/capitol/subCapitol/teamRelationships
        // sin dispatchar a Redux (NO relacionar con la OT del modal).
        setAutoFillData(result);

        // Apply auto-fill values to form
        if (result.communication) {
          const comm = result.communication;

          // Set groupInter (interlocutor group) - both value and visual dropdown
          if (comm.interlocutorGroupIDs && comm.interlocutorGroupIDs.length > 0) {
            const groupInterId = comm.interlocutorGroupIDs[0];
            setValue('groupInter', groupInterId);

            // Find the label from initialData and update dropdown visually
            const groupInterOption = initialData.interlocutorGroups?.find(
              (g) => g?.id === groupInterId
            );
            if (groupInterOption) {
              groupInterRef.current?.setSelectedOption({
                label: groupInterOption.name || '',
                value: groupInterId,
              });
            }
          }

          // Set media (communication media) - both value and visual dropdown
          if (comm.mediaOfCommunicationIDs && comm.mediaOfCommunicationIDs.length > 0) {
            const mediaId = comm.mediaOfCommunicationIDs[0];
            setValue('media', mediaId);

            // Find the label from initialData and update dropdown visually
            const mediaOption = initialData.mediaOfCommunications?.find(
              (m) => m?.id === mediaId
            );
            if (mediaOption) {
              mediaRef.current?.setSelectedOption({
                label: mediaOption.name || '',
                value: mediaId,
              });
            }
          }

          // Set sendStatus
          if (comm.sendStatus) {
            setValue('sendStatus', comm.sendStatus as CommunicationSendStatus);
          }
        }
      }
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error fetching auto-fill data:', error);
    }
  }, [fetchDataToAutoFill, setValue, initialData]);

  return (
    <ContainerForm onSubmit={handleSubmit(onFormSubmit)}>
      <FieldsContainer>
        <ContainerWithButton>
          <BasicInfo
            control={control}
            register={register}
            errors={errors}
            refs={{ groupInterRef, mediaRef }}
            showOperatorPicker={
              namingPersistForm === formNaming.NEW_DUPLICATE_COMMUNICATION_FORM
            }
            onOperatorChange={(name) => setValue('operator', name)}
          />
          {showAutoFillButton && (
            <MainButton
              variant="secondary"
              text={t('workOrder.autoFill')}
              onClick={autoFillFunction}
              type="button"
            />
          )}
        </ContainerWithButton>

        <DoubleColumnContainer>
          <Description
            control={control}
            setValue={setValue}
            register={register}
            errors={errors}
            initialData={initialData}
            autoFillData={autoFillData}
          />
          <CreateOT
            control={control}
            setValue={setValue}
            register={register}
            errors={errors}
            namingPersistForm={namingPersistForm}
            namingTabsType={namingTabsType}
            selectorIndexTab={effectiveIndexTab}
            isNecessaryTagSpecial={isNecessaryTagSpecial}
            teamDropDownRef={teamDropDownRef}
            autoFillData={autoFillData}
            onUserClickTab={handleUserClickTab}
          />
        </DoubleColumnContainer>
      </FieldsContainer>

      <ButtonsContainer>
        <MainButton text={t('common:buttons.continue')} type="submit" />
      </ButtonsContainer>
    </ContainerForm>
  );
};

export default NewCommunicationForm;
